import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import history from '~/services/history';

export const UrlContext = createContext();

export function UrlProvider({ children }) {
  const [urls, setUrls] = useState([]);
  const [urlAtual, setUrlAtual] = useState(null);
  const urlAtualRef = useRef(null);

  /**
   * Rastreia mudanças de URL automaticamente
   * Quando a URL muda:
   * - Se mudou apenas querystring na MESMA tela (pathname igual), atualiza a última URL (não empilha)
   * - Se mudou de tela (pathname diferente) via PUSH, empilha
   * - Se mudou via REPLACE (redirect/replace), substitui a última
   */
  useEffect(() => {
    const buildUrlCompleta = location =>
      `${location?.pathname || ''}${location?.search || ''}`;

    const getPathnameFromUrl = (urlCompleta) => {
      if (!urlCompleta) return null;
      return String(urlCompleta).split('?')[0] || null;
    };

    const registrar = (location, action) => {
      const urlCompleta = buildUrlCompleta(location);
      if (!urlCompleta || urlAtualRef.current === urlCompleta) return;

      urlAtualRef.current = urlCompleta;
      setUrlAtual(urlCompleta);

      // Mantém histórico por TELA (pathname). Mudanças de querystring não devem criar novas entradas.
      setUrls(prevUrls => {
        const ultimaUrl = prevUrls[prevUrls.length - 1];
        const ultimaPath = getPathnameFromUrl(ultimaUrl);
        const pathAtual = location?.pathname || getPathnameFromUrl(urlCompleta);

        // Primeira URL
        if (!ultimaUrl) return [urlCompleta];

        // Mesmo pathname => atualiza última (replace lógico)
        if (ultimaPath && pathAtual && ultimaPath === pathAtual) {
          if (ultimaUrl === urlCompleta) return prevUrls;
          return [...prevUrls.slice(0, -1), urlCompleta];
        }

        // Pathname diferente:
        // - REPLACE substitui a última entrada (evita "voltar" para a mesma tela)
        // - PUSH empilha normalmente
        if (action === 'REPLACE') {
          return [...prevUrls.slice(0, -1), urlCompleta];
        }

        return [...prevUrls, urlCompleta];
      });
    };

    // registra a rota inicial + escuta mudanças
    registrar(history.location);
    const unlisten = history.listen((location, action) =>
      registrar(location, action)
    );

    return () => {
      if (typeof unlisten === 'function') unlisten();
    };
  }, []);

  /**
   * Empipha manualmente uma URL (compatibilidade com código existente)
   * Prioriza a captura automática, mas permite sobrescrita se necessário
   */
  function empilhaUrl(url) {
    setUrls(prevUrls => {
      const ultimaUrl = prevUrls[prevUrls.length - 1];
      if (ultimaUrl !== url) {
        return [...prevUrls, url];
      }
      return prevUrls;
    });
  }

  /**
   * Obtém a URL anterior ANTES da atual
   * Retorna com pathname e query params separados para facilitar navegação
   */
  function obterUrlPaginaAnterior() {
    const valorDefault = {
      urlCompleta: null,
      urlBase: null,
      queryParam: null,
    };

    if (urls?.length < 2) {
      return valorDefault;
    }

    // Obtém a penúltima URL (a anterior)
    const urlAnterior = String(urls[urls.length - 2]);

    if (urlAnterior) {
      // Remove a última URL do histórico
      setUrls(prevUrls => prevUrls.slice(0, -1));

      const [urlBaseRaw, queryParam] = urlAnterior.split('?');

      // Normaliza para garantir apenas UMA barra no começo (evita #//...)
      const normalizePathname = (path) => {
        const value = String(path || '').trim();
        if (!value) return null;
        if (value === '/') return '/';
        return `/${value.replace(/^\/+/, '')}`;
      };

      const urlBase = normalizePathname(urlBaseRaw);

      return {
        urlCompleta: urlAnterior,
        urlBase,
        queryParam: queryParam ? `?${queryParam}` : null,
      };
    }

    return valorDefault;
  }

  /**
   * Limpa o histórico de URLs
   */
  function limparHistoricoUrls() {
    setUrls([]);
  }

  /**
   * Retorna o histórico completo de URLs (para debug)
   */
  function obterHistoricoUrls() {
    return urls;
  }

  return (
    <UrlContext.Provider
      value={{
        urls,
        urlAtual,
        empilhaUrl,
        obterUrlPaginaAnterior,
        limparHistoricoUrls,
        obterHistoricoUrls,
      }}
    >
      {children}
    </UrlContext.Provider>
  );
}

export function useUrls() {
  const context = useContext(UrlContext);
  
  if (!context) {
    throw new Error('useUrls deve ser utilizado dentro de UrlProvider');
  }

  const { 
    urls, 
    urlAtual,
    empilhaUrl, 
    obterUrlPaginaAnterior,
    limparHistoricoUrls,
    obterHistoricoUrls,
  } = context;

  return {
    urls,
    urlAtual,
    empilhaUrl,
    obterUrlPaginaAnterior,
    limparHistoricoUrls,
    obterHistoricoUrls,
  };
}
